import apiClient from "./client";
import type { EventFamily, EventCeremony } from "./eventSettings";
import type { EventTemplateDefinition, EventTemplateId } from "@/lib/event-templates";

import type { PlanType } from '@/models/Plan';

/** JUB-169: one enabled moment of the event, as the owner's event list returns it. */
export type EventMomentType = 'civil' | 'religious' | 'baptism' | 'party';

export interface EventMoment {
  type: EventMomentType;
  /** The stored custom title ("Cununia religioasă și Botezul"); null means the default label. */
  title: string | null;
}

export interface EventOnboardingSummary {
  status: 'started' | 'completed';
  step: string | null;
  progress: number;
  completedSteps: string[];
  skippedSteps: string[];
}

export interface Event {
  uuid: string;
  name: string;
  /** JUB-154: null while the couple has not decided the date. */
  date: string | null;
  type: number;
  description?: string;
  starBride?: string;
  starGroom?: string;
  starBaptism?: string;
  starBaptism2?: string;
  includesWedding?: boolean;
  includesBaptism?: boolean;
  babyCount?: number | null;
  currency: string;
  country: string;
  defaultLanguage?: 'ro' | 'en';
  url: string;
  live?: boolean;
  active?: boolean;
  giftsActive?: boolean;
  theme?: string;
  fontFamily?: string | null;
  fontScale?: string | null;
  story?: string | null;
  gallery?: string[];
  passwordEnabled?: boolean;
  searchable?: boolean;
  registryPublic?: boolean;
  rsvpCutoffDate?: string | null;
  createdDate?: string;
  plan?: PlanType;
  planPaymentId?: number;
  /** JUB-157: hidden from the dashboard list; nothing else changes. */
  archivedAt?: string | null;
  /** JUB-157: set while the event is in the Trash. */
  deletedAt?: string | null;
  /** JUB-157: end of the Trash window (permanent deletion after it). */
  retentionUntil?: string | null;
  /** JUB-154: wizard progress (owner only); null for events created without the wizard. */
  onboarding?: EventOnboardingSummary | null;
  template?: EventTemplateDefinition;
  family?: EventFamily;
  ceremonies?: {
    civil?: EventCeremony;
    religious?: EventCeremony;
    party?: EventCeremony;
    /** JUB-154: a baptism held separately from the religious wedding. */
    baptism?: EventCeremony;
  };
  stats?: {
    confirmedGuests: number;
    totalGifts: number;
    totalContributions: number;
  };
  /** JUB-169: enabled moments in display order (civil, religious, baptism, party). */
  moments?: EventMoment[];
}

export interface CreateEventRequest {
  name: string;
  date: string;
  type?: number;
  description?: string;
  starBride?: string;
  starGroom?: string;
  starBaptism?: string;
  starBaptism2?: string;
  includesWedding?: boolean;
  includesBaptism?: boolean;
  babyCount?: number;
  currency?: string;
  country?: string;
  url?: string;
  defaultLanguage?: 'ro' | 'en';
  rsvpCutoffDate?: string | null;
  /** JUB-36: default template picked from the creation modal's event type, not chosen twice by the customer. */
  theme?: EventTemplateId;
}

export interface ApiErrorResponse {
  error: {
    code: string;
    message_key: string;
    message?: string;
    errors?: Record<string, string[]>;
  };
}

/**
 * Get all events for the current user
 */
export async function getEvents(options: { archived?: boolean } = {}): Promise<Event[]> {
  const response = await apiClient.get<{ items: Event[] } | ApiErrorResponse>(
    options.archived ? '/events?archived=1' : '/events'
  );
  const data = response.data;
  
  // Check if response is an error
  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to fetch events');
  }
  
  // Response is wrapped in "items" property
  if (data && typeof data === 'object' && 'items' in data && Array.isArray(data.items)) {
    return data.items;
  }
  
  return [];
}

/**
 * Get a single event by ID
 */
export async function getEvent(id: string | number): Promise<Event> {
  const response = await apiClient.get<Event | ApiErrorResponse>(`/events/${id}`);
  const data = response.data;
  
  // Check if response is an error
  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to fetch event');
  }
  
  // Response is direct event object (RESTful)
  return data as Event;
}

/**
 * Create a new event
 */
export async function createEvent(eventData: CreateEventRequest): Promise<Event> {
  const response = await apiClient.post<Event | ApiErrorResponse>('/events', eventData);
  const data = response.data;
  
  // Check if response is an error
  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to create event');
  }
  
  // Response is direct event object (RESTful)
  if (data && typeof data === 'object' && 'uuid' in data) {
    return data as Event;
  }
  
  throw new Error('Invalid response format from create event API');
}

/**
 * Update an existing event
 */
export async function updateEvent(
  id: string | number,
  // JUB-154: date: null clears an undecided date.
  eventData: Partial<Omit<CreateEventRequest, 'date'>> & { date?: string | null },
): Promise<Event> {
  const response = await apiClient.put<Event | ApiErrorResponse>(`/events/${id}`, eventData);
  const data = response.data;
  
  // Check if response is an error
  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to update event');
  }
  
  // Response might have message_key in addition to event data
  // Extract just the event data if it's wrapped
  if ('message_key' in data && 'uuid' in data) {
    return data as Event;
  }
  
  return data as Event;
}

/**
 * Move an event to the Trash (JUB-157: restorable for 30 days).
 */
export async function deleteEvent(id: string | number): Promise<void> {
  await apiClient.delete(`/events/${id}`);
}

/** An event in the Trash, as returned by GET /events/trash. */
export interface TrashedEvent extends Event {
  restorable: boolean;
}

/**
 * The owner's events in the Trash (JUB-157).
 */
export async function getTrashedEvents(): Promise<TrashedEvent[]> {
  const response = await apiClient.get<{ items: TrashedEvent[] }>('/events/trash');
  return Array.isArray(response.data?.items) ? response.data.items : [];
}

/**
 * Restore an event from the Trash; its plan comes back with it (JUB-157).
 */
export async function restoreEvent(id: string): Promise<Event> {
  const response = await apiClient.post<Event>(`/events/${id}/restore`);
  return response.data;
}

/**
 * Permanently delete an event that is in the Trash (JUB-157). Its purchase
 * stays consumed and can never be used for another event.
 */
export async function deleteEventPermanently(id: string): Promise<void> {
  await apiClient.delete(`/events/${id}/permanent`);
}

/**
 * Archive / unarchive: only hides the event from the dashboard list (JUB-157).
 */
export async function setEventArchived(id: string, archived: boolean): Promise<Event> {
  const response = await apiClient.post<Event>(`/events/${id}/${archived ? 'archive' : 'unarchive'}`);
  return response.data;
}

/**
 * Publish an event (make its public page live for guests)
 */
export async function publishEvent(id: string | number): Promise<Event> {
  const response = await apiClient.post<Event | ApiErrorResponse>(`/events/${id}/publish`);
  const data = response.data;

  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to publish event');
  }

  return data as Event;
}

/**
 * Unpublish an event (take its public page offline for guests)
 */
export async function unpublishEvent(id: string | number): Promise<Event> {
  const response = await apiClient.post<Event | ApiErrorResponse>(`/events/${id}/unpublish`);
  const data = response.data;

  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to unpublish event');
  }

  return data as Event;
}

/**
 * Reset event language to user's default language
 */
export async function resetEventLanguage(id: string | number): Promise<Event> {
  const response = await apiClient.post<Event | ApiErrorResponse>(`/events/${id}/reset-language`);
  const data = response.data;
  
  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to reset event language');
  }
  
  return data as Event;
}

